import { Spinner } from '../components/feedback';
import { useState } from 'react';
import type { ExtraPortInput, VoicePreset } from '@mcpanel/core';
import { api } from '../api/client';
import type { ChangeRequest, ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { ErrorAlert } from '../components/ui';
import { usePoll } from '../hooks/usePoll';
import { PendingForServer, ReviewDialog } from './ServerManage';
import { WebSitesSection } from './WebSitesSection';

export function PortsTab({ server }: { server: ServerView }) {
  const result = usePoll(() => api.ports(server.id), 10000, [server.id, server.revision]);
  const { canOperate, notify } = useApp();
  const [request, setRequest] = useState<ChangeRequest>();
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [protocol, setProtocol] = useState<'udp' | 'tcp'>('udp');
  const [internalPort, setInternalPort] = useState(24454);
  const [editing, setEditing] = useState<ExtraPortInput>();
  const data = result.data;
  const current = data?.extraPorts.map(({ address: _address, ...port }) => port) ?? [];

  async function plan(extraPorts: ExtraPortInput[]) {
    setBusy(true);
    try {
      setRequest(await api.createRequest('change_ports', { extraPorts }, server.id));
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  }
  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      notify('コピーしました');
    } catch (error) {
      notify(errorMessage(error), true);
    }
  }
  function addPreset(preset: VoicePreset) {
    const suggestion = data?.presets.find((p) => p.id === preset);
    if (!suggestion) return;
    void plan([...current, { ...suggestion, id: crypto.randomUUID(), preset }]);
  }
  function edit(port: ExtraPortInput) {
    setEditing(port);
    setName(port.name);
    setProtocol(port.protocol);
    setInternalPort(port.internalPort);
  }
  function saveManual() {
    const entry = { ...editing, id: editing?.id ?? crypto.randomUUID(), name, protocol, internalPort };
    void plan(editing ? current.map((p) => (p.id === editing.id ? entry : p)) : [...current, entry]);
  }

  return (
    <div className="stack">
      <PendingForServer serverId={server.id} />
      <ErrorAlert error={result.error} />
      {data && (
        <>
          <section className="card card-body stack">
            <h3 data-search-id="ports">ポート</h3>
            {!data.extraPorts.length && <p>追加ポートはありません。</p>}
            {data.extraPorts.map((port) => (
              <div key={port.id}>
                <p>
                  {port.name}：{port.protocol.toUpperCase()} {port.publicPort} → 内部 {port.internalPort}
                </p>
                <p>プレイヤーに伝える接続先：{port.address}</p>
                <button className="btn" onClick={() => void copy(port.address)}>
                  接続先をコピー
                </button>{' '}
                {canOperate && (
                  <>
                    <button
                      className="btn"
                      disabled={busy}
                      onClick={() => edit(current.find((p) => p.id === port.id)!)}
                    >
                      {busy && <Spinner />}
                      変更
                    </button>{' '}
                    <button
                      className="btn danger"
                      disabled={busy}
                      onClick={() => void plan(current.filter((p) => p.id !== port.id))}
                    >
                      {busy && <Spinner />}
                      削除
                    </button>
                  </>
                )}
              </div>
            ))}
          </section>
          {canOperate && (
            <section className="card card-body stack">
              <h3>追加・変更</h3>
              {data.presets.map((preset) => (
                <button key={preset.id} className="btn" disabled={busy} onClick={() => addPreset(preset.id)}>
                  {busy && <Spinner />}
                  {preset.name} を追加
                </button>
              ))}
              <p>
                近接 VC の Mod・プラグインを先に追加するとプリセットを提案します。公開番号は Panel が 24400〜24499
                から割り当てます。
              </p>
              <div className="field">
                <label htmlFor="port-name">名前</label>
                <input
                  data-search-id="port-name"
                  id="port-name"
                  className="input"
                  maxLength={100}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="port-protocol">プロトコル</label>
                <select
                  data-search-id="port-protocol"
                  id="port-protocol"
                  className="input"
                  value={protocol}
                  disabled={Boolean(editing?.preset)}
                  onChange={(e) => setProtocol(e.target.value as 'udp' | 'tcp')}
                >
                  <option value="udp">UDP</option>
                  <option value="tcp">TCP</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="port-internal">内部ポート</label>
                <input
                  data-search-id="port-internal"
                  id="port-internal"
                  className="input"
                  type="number"
                  min={1}
                  max={65535}
                  value={internalPort}
                  onChange={(e) => setInternalPort(Number(e.target.value))}
                  aria-describedby="port-internal-error"
                  aria-invalid={!Number.isInteger(internalPort) || internalPort < 1 || internalPort > 65535}
                />
                <span className="small muted">番号（1〜65535）</span>
                {(!Number.isInteger(internalPort) || internalPort < 1 || internalPort > 65535) && (
                  <div id="port-internal-error" role="alert" style={{ color: 'var(--danger)' }}>
                    1〜65535 の整数を入力してください。
                  </div>
                )}
              </div>
              <p>
                手動ポートは、Mod 側の設定をファイルタブで内部ポートに合わせてください。変更すると Minecraft
                が再起動します。
              </p>
              <button
                className="btn primary"
                disabled={
                  busy || !name.trim() || !Number.isInteger(internalPort) || internalPort < 1 || internalPort > 65535
                }
                onClick={saveManual}
              >
                {busy && <Spinner />}
                {editing ? '変更内容を確認' : '追加内容を確認'}
              </button>
              {editing && (
                <button
                  className="btn"
                  onClick={() => {
                    setEditing(undefined);
                    setName('');
                  }}
                >
                  編集をやめる
                </button>
              )}
            </section>
          )}
        </>
      )}
      <WebSitesSection server={server} />
      {request && <ReviewDialog request={request} onClose={() => setRequest(undefined)} />}
    </div>
  );
}
